import React, { useRef, useEffect } from "react";
import { useFormContext, useController } from "react-hook-form";
import "./Form.css";
import { TextCss } from "../../components/GlobalCss/TextCss";

interface EmailInputProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  disabled?: boolean;
  validation?: {
    required?: string | boolean;
    pattern?: { value: RegExp; message: string };
    validate?: (value: any) => boolean | string;
    [key: string]: any;
  };
  className?: string;
}

const EmailInput: React.FC<EmailInputProps> = ({
  label = "Email",
  name,
  required = false,
  placeholder = "Enter your email...",
  requiredMessage = "This field is required",
  disabled = false,
  validation = {},
  className = "",
  ...rest
}) => {
  const {
    control,
    formState: { isSubmitted },
    setError,
    clearErrors,
    trigger,
  } = useFormContext();
  const timeoutRef = useRef<NodeJS.Timeout|null>(null);

  const {
    field: { onChange, value, onBlur, ...fieldRest },
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
    rules: {
      required: required ? requiredMessage : false,
      maxLength: { value: 50, message: "Email cannot exceed 50 characters" },
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Please enter a valid email address",
        ...validation.pattern,
      },
      ...validation,
    },
  });

  const showErrorMessage =
    ((isTouched || isSubmitted) && error) || error?.type === 'maxLength'
      ? error?.message
      : undefined;

  // Validate on blur explicitly rather than relying on the parent form's
  // `mode` (most forms use the RHF default "onSubmit", which never
  // populates `fieldState.error` until a submit attempt — and Save stays
  // disabled while the form is invalid, so that submit can never happen).
  const handleBlur = () => {
    onBlur();
    trigger(name);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLowerCase();
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const maxLengthRule = validation?.maxLength;
    const maxLength = maxLengthRule?.value || maxLengthRule || 50;
    
    if (val.length > maxLength) {
      // Manual trigger error
      const message = maxLengthRule?.message || `Maximum length of ${maxLength} characters reached`;
      setError(name, { type: 'maxLength', message });

      timeoutRef.current = setTimeout(() => {
        clearErrors(name);
      }, 1000);

      return; // Block input
    }

    // Clear error if we were showing a maxLength error and now we are safe
    if (error?.type === 'maxLength') {
      clearErrors(name);
    }

    onChange(val);

    // Must run unconditionally, not just "if (error)" -- react-hook-form's
    // formState.isValid lags a full render behind field.onChange under
    // mode: "onChange" (a known RHF quirk), so a Save button gated on
    // isValid stays disabled for the keystroke that actually made the form
    // valid, only catching up on the *next* interaction. Calling trigger()
    // here forces isValid to resolve in the same interaction.
    trigger(name);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className={TextCss.black.sub_heading_600_14}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      <input
        type="email"
        disabled={disabled}
        className={`form-control ${showErrorMessage ? "is-invalid" : ""}`}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={handleInputChange}
        onBlur={handleBlur}
        {...fieldRest}
        {...rest}
      />
      {showErrorMessage && (
        <div className="invalid-feedback">{showErrorMessage as string}</div>
      )}
    </div>
  );
};

export default EmailInput;
